import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { FiChevronDown, FiX, FiCheck, FiFileText } from 'react-icons/fi';
import { ExtraDoc } from './QuoteStepper';
import UploadDocumentZone from './UploadDocumentZone';
import { getCompanyHistory, saveCompanySettings } from '../../services/quoteProposalService';
import '../../styles/QuoteProposal.css';
import t1Img from '../../assets/images/qp-template1.svg';
import t2Img from '../../assets/images/qp-template2.png';
import t3Img from '../../assets/images/qp-template3.svg';
import T1Img1 from '../../assets/images/Insurance Template - 1.svg';
import T1Img2 from '../../assets/images/Insurance Template - 4.svg';
import T1Img3 from '../../assets/images/Insurance Template - 6.svg';
import T1Img4 from '../../assets/images/Insurance Template - 8.svg';
import T1Img5 from '../../assets/images/Insurance Template - 9.svg';
import T1Img6 from '../../assets/images/Insurance Template - 7.svg';
import T2Img1 from '../../assets/images/Insurance-template2-img2.png';
import T2Img2 from '../../assets/images/Insurance-template2-img3.png';
import T2Img3 from '../../assets/images/Insurance-template2-img4.png';
import T2Img4 from '../../assets/images/Insurance-template2-img5.png';
import T2Img5 from '../../assets/images/Insurance-template2-img6.png';
import T3Img1 from '../../assets/images/A4 - 3.svg';
import T3Img2 from '../../assets/images/A4 - 4.svg';

const svgBase = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const FiEye: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const FiTrash2: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const FiInfo: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const FiRuby: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <polygon points="6 3 18 3 22 9 12 22 2 9" />
  </svg>
);
const FiChevronLeft: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}><polyline points="15 18 9 12 15 6" /></svg>
);
const FiChevronRight: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}><polyline points="9 18 15 12 9 6" /></svg>
);

/* ── Template options defined outside component (stable reference) ── */
const TEMPLATE_OPTIONS = [
  { id: '1', label: 'Modern Grid', desc: 'Clean grid layout with key metrics', img: t1Img, previewImages: [T1Img1, T1Img2, T1Img3, T1Img4, T1Img5, T1Img6] },
  { id: '2', label: 'Classic Compare', desc: 'Side-by-side comparison with breakdowns', img: t2Img, previewImages: [T2Img1, T2Img2, T2Img3, T2Img4, T2Img5] },
  { id: '3', label: 'Modern Template', desc: 'Elegant modern design with rich visuals', img: t3Img, previewImages: [T3Img1, T3Img2] },
];

/* ── Props interface — flat (matches QuoteStepper passthrough) ── */
export interface Step2Props {
  fileNamesForDataTable: string[];
  fileURL: string;
  teamMemberList: any;
  teamMember: string;
  proposalTeamMembers: any[];
  selectedTemplate: string;
  selectedCustomTemplate: number;
  selectedCustomTemplateName: string;
  selectedCustomTemplateImages: string[];
  customTemplateList: any[];
  videoUrl: string;
  extraDocumentList: ExtraDoc[];
  uploadDocLoading: boolean;
  customBlocks: any[];
  transactionId: string;
  dynamicLinks?: { description: string; url: string }[];
  onTemplateChange: (id: string, name: string, images: string[], customId?: number) => void;
  onSignatureChange: (v: string) => void;
  onTeamMembersChange: (members: any[]) => void;
  onVideoUrlChange: (v: string) => void;
  onExtraDocFileDrop: (file: File) => void;
  onExtraDocRemove: (i: number) => void;
  onCustomBlocksChange: (blocks: any[]) => void;
  onDynamicLinksChange?: (links: { description: string; url: string }[]) => void;
  onInvalidPdfFile?: () => void;
  onViewDocumentClicked?: (fileName: string) => void;
}

const Step2_SignatureDocsVideo: React.FC<Step2Props> = ({
  fileNamesForDataTable, fileURL,
  teamMemberList, teamMember, proposalTeamMembers,
  selectedTemplate, selectedCustomTemplate, selectedCustomTemplateName, selectedCustomTemplateImages,
  customTemplateList,
  videoUrl,
  extraDocumentList, uploadDocLoading,
  transactionId,
  dynamicLinks = [],
  onTemplateChange, onSignatureChange, onTeamMembersChange,
  onVideoUrlChange,
  onExtraDocFileDrop, onExtraDocRemove,
  onDynamicLinksChange,
  onInvalidPdfFile,
  onViewDocumentClicked,
}) => {
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [linksExpanded, setLinksExpanded] = useState(true);
  const [iconDropdownOpen, setIconDropdownOpen] = useState(false);
  const [iconDropdownRect, setIconDropdownRect] = useState<DOMRect | null>(null);
  const [editIconDropdownOpen, setEditIconDropdownOpen] = useState<number | null>(null);
  const [editIconDropdownRect, setEditIconDropdownRect] = useState<DOMRect | null>(null);
  const newIconBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      setIconDropdownOpen(false);
      setEditIconDropdownOpen(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const [newLinkDescription, setNewLinkDescription] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [linkErrors, setLinkErrors] = useState({ description: '', url: '' });

  const iconOptions = [
    { icon: '🔗', label: 'Link',          value: 'link',     piIcon: 'pi pi-link',        color: '#6c757d' },
    { icon: '▶️', label: 'Video',         value: 'video',    piIcon: 'pi pi-play',        color: '#dc3545' },
    { icon: '📄', label: 'Document',      value: 'document', piIcon: 'pi pi-file-pdf',    color: '#dc3545' },
    { icon: '📋', label: 'Page/Proposal', value: 'proposal', piIcon: 'pi pi-file',        color: '#17a2b8' },
    { icon: '📁', label: 'Folder',        value: 'folder',   piIcon: 'pi pi-folder',      color: '#ffc107' },
    { icon: '💳', label: 'Payment',       value: 'payment',  piIcon: 'pi pi-credit-card', color: '#28a745' },
    { icon: '🔑', label: 'Portal/Login',  value: 'portal',   piIcon: 'pi pi-key',         color: '#007bff' },
  ];
  const [newLinkIcon, setNewLinkIcon] = useState(iconOptions[0]);

  /* local state for row-level edit — mirrors reference implementation */
  const [localLinks, setLocalLinks] = useState<any[]>(() =>
    (dynamicLinks || []).map((l: any) => ({
      ...l,
      selectedIcon: iconOptions.find(o => o.value === l.icon) || iconOptions[0],
      isEditing: false,
      editDescription: l.description,
      editUrl: l.url,
      editSelectedIcon: iconOptions.find(o => o.value === l.icon) || iconOptions[0],
    }))
  );
  /* keep localLinks in sync when parent dynamicLinks changes (e.g. draft load) */
  useEffect(() => {
    setLocalLinks((dynamicLinks || []).map((l: any) => ({
      ...l,
      selectedIcon: iconOptions.find(o => o.value === l.icon) || iconOptions[0],
      isEditing: false,
      editDescription: l.description,
      editUrl: l.url,
      editSelectedIcon: iconOptions.find(o => o.value === l.icon) || iconOptions[0],
    })));
  }, [dynamicLinks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const pushLinks = (updated: any[]) => {
    setLocalLinks(updated);
    onDynamicLinksChange?.(updated.map(l => ({ icon: l.selectedIcon?.value || 'link', description: l.description, url: l.url })));
  };

  const onAddLink = () => {
    const errs = { description: '', url: '' };
    if (!newLinkDescription.trim()) errs.description = 'Description is required.';
    if (!newLinkUrl.trim()) errs.url = 'Link is required.';
    else if (!/^https?:\/\/.+/.test(newLinkUrl.trim())) errs.url = 'Please enter a valid URL (starting with http:// or https://).';
    else if (localLinks.some(l => l.url === newLinkUrl.trim())) errs.url = 'This URL has already been added.';
    if (errs.description || errs.url) { setLinkErrors(errs); return; }
    const newItem = {
      icon: newLinkIcon.value, description: newLinkDescription.trim(), url: newLinkUrl.trim(),
      selectedIcon: newLinkIcon, isEditing: false,
      editDescription: newLinkDescription.trim(), editUrl: newLinkUrl.trim(), editSelectedIcon: newLinkIcon,
    };
    pushLinks([...localLinks, newItem]);
    setNewLinkIcon(iconOptions[0]); setNewLinkDescription(''); setNewLinkUrl(''); setLinkErrors({ description: '', url: '' });
  };

  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      if (parsed.hostname.includes('youtu.be'))
        return `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`;
      if (parsed.hostname.includes('youtube.com')) {
        if (parsed.searchParams.get('v'))
          return `https://www.youtube.com/embed/${parsed.searchParams.get('v')}`;
        if (parsed.pathname.startsWith('/shorts/'))
          return `https://www.youtube.com/embed/${parsed.pathname.split('/')[2]}`;
        if (parsed.pathname.startsWith('/embed/')) return url;
      }
      if (parsed.hostname.includes('vimeo.com')) {
        const id = parsed.pathname.split('/').filter(Boolean)[0];
        return `https://player.vimeo.com/video/${id}`;
      }
      return '';
    } catch { return ''; }
  };
  const [extraDocsExpanded, setExtraDocsExpanded] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [previewGalleria, setPreviewGalleria] = useState<{ title: string; images: string[]; idx: number } | null>(null);

  type PendingTemplate = { id: string; name: string; images: string[]; customId: number };
  const [pendingTemplate, setPendingTemplate] = useState<PendingTemplate | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const openTemplateDialog = () => {
    setPendingTemplate({
      id: selectedTemplate,
      name: selectedCustomTemplateName || TEMPLATE_OPTIONS.find(t => t.id === selectedTemplate)?.label || 'Modern Grid',
      images: selectedCustomTemplateImages || [],
      customId: selectedCustomTemplate,
    });
    setTemplateDialogOpen(true);
  };

  const handleTemplateSave = async () => {
    if (!pendingTemplate) return;
    setIsSavingTemplate(true);
    try {
      const existing = await getCompanyHistory();
      await saveCompanySettings({
        ...(existing || {}),
        templateId: pendingTemplate.id,
        customTemplateId: pendingTemplate.customId || undefined,
        customTemplateName: pendingTemplate.customId ? pendingTemplate.name : undefined,
      });
      onTemplateChange(pendingTemplate.id, pendingTemplate.name, pendingTemplate.images, pendingTemplate.customId || undefined);
      setTemplateDialogOpen(false);
    } catch {
      // error is handled by service (401 → redirect)
    } finally {
      setIsSavingTemplate(false);
    }
  };
  /* Refs for portal positioning */
  const membersRef         = useRef<HTMLDivElement>(null);
  const sigRef             = useRef<HTMLDivElement>(null);
  const membersDropdownRef = useRef<HTMLDivElement>(null);
  const sigDropdownRef     = useRef<HTMLDivElement>(null);
  const [membersRect, setMembersRect] = useState<DOMRect | null>(null);
  const [sigRect, setSigRect]         = useState<DOMRect | null>(null);

  const openMembers = () => {
    if (membersRef.current) setMembersRect(membersRef.current.getBoundingClientRect());
    setMembersOpen(v => !v);
    setSigOpen(false);
  };
  const openSig = () => {
    if (sigRef.current) setSigRect(sigRef.current.getBoundingClientRect());
    setSigOpen(v => !v);
    setMembersOpen(false);
  };

  /* Close dropdowns on outside click.
     We check both the trigger ref AND the portal dropdown ref so that clicking
     inside the portal doesn't dismiss it before the option's click can fire. */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        membersOpen &&
        membersRef.current && !membersRef.current.contains(t) &&
        membersDropdownRef.current && !membersDropdownRef.current.contains(t)
      ) setMembersOpen(false);
      if (
        sigOpen &&
        sigRef.current && !sigRef.current.contains(t) &&
        sigDropdownRef.current && !sigDropdownRef.current.contains(t)
      ) setSigOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [membersOpen, sigOpen]);

  /* Galleria keyboard navigation */
  useEffect(() => {
    if (!previewGalleria) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')
        setPreviewGalleria(p => p && p.images.length > 1 ? { ...p, idx: (p.idx - 1 + p.images.length) % p.images.length } : p);
      else if (e.key === 'ArrowRight')
        setPreviewGalleria(p => p && p.images.length > 1 ? { ...p, idx: (p.idx + 1) % p.images.length } : p);
      else if (e.key === 'Escape')
        setPreviewGalleria(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [previewGalleria]);

  /* Team members helpers */
  const availableMembers: any[] = teamMemberList?.members?.filter((m: any) => m.name?.trim()) || [];
  const selectedSignatureMember = availableMembers.find((m: any) => m.name === teamMember);

  const matchMember = useCallback((a: any, b: any): boolean => {
    if (a.name && b.name && a.name === b.name) return true;
    if (a.id && b.id && String(a.id) === String(b.id)) return true;
    return false;
  }, []);

  const toggleMember = useCallback((member: any) => {
    const isSelected = proposalTeamMembers.some((m: any) => matchMember(m, member));
    if (isSelected) {
      onTeamMembersChange(proposalTeamMembers.filter((m: any) => !matchMember(m, member)));
    } else {
      onTeamMembersChange([...proposalTeamMembers, member]);
    }
  }, [proposalTeamMembers, onTeamMembersChange, matchMember]);

  /* Signature preview */
  const renderSignature = (member: any) => {
    if (!member) return null;
    const opt = member.selectedOption;
    if (opt === 'type' && member.typedSignature)
      return <span style={{ fontFamily: 'cursive', fontSize: 22, color: 'var(--text-primary)' }}>{member.typedSignature}</span>;
    if (opt === 'draw' && member.drawnSignature)
      return <img src={member.drawnSignature} alt="Signature" style={{ maxHeight: 60 }} />;
    if (opt === 'upload' && member.uploadedSignature)
      return <img src={member.uploadedSignature} alt="Signature" style={{ maxHeight: 60 }} />;
    return null;
  };

  /* File URLs list */
  const fileUrls = fileURL ? fileURL.split(',') : [];

  /* Template label */
  const templateLabel = selectedCustomTemplateName ||
    TEMPLATE_OPTIONS.find(t => t.id === selectedTemplate)?.label ||
    'Modern Grid';

  return (
    <>
      <div className="qp-step-shell">
      {/* ── Section 1: Uploaded Documents ── */}
      <div className="qp-inner-card">
        <h3 className="qp-section__title">Uploaded Documents</h3>
        <p className="qp-section__subtitle">Review documents from Step 1</p>

        {fileNamesForDataTable.length === 0 ? (
          <div className="qp-empty-state" style={{ padding: 'var(--space-4) 0' }}>
            <FiFileText size={20} />
            <p>No documents uploaded</p>
          </div>
        ) : (
          <table className="qp-table qp-table--compact" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th className="qp-table__th">#</th>
                <th className="qp-table__th">File Name</th>
                <th className="qp-table__th" style={{ width: 80, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {fileNamesForDataTable.map((name, i) => (
                <tr key={i} className="qp-table__row">
                  <td className="qp-table__td" style={{ width: 40 }}>{i + 1}</td>
                  <td className="qp-table__td">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FiFileText size={13} style={{ color: '#ef4444', flexShrink: 0 }} />
                      <span style={{ fontSize: 13 }}>{name}</span>
                    </div>
                  </td>
                  <td className="qp-table__td" style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="qp-pill-btn qp-pill-btn--teal"
                      onClick={() => {
                        if (onViewDocumentClicked) {
                          onViewDocumentClicked(name);
                        } else if (fileUrls[i]) {
                          window.open(fileUrls[i], '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <FiEye size={11} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Section 2: Template & Signature ── */}
      <div className="qp-inner-card">
        <h3 className="qp-section__title">Template &amp; Signature</h3>
        <p className="qp-section__subtitle">Configure proposal template and e-signature</p>

        <div className="qp-form-row">
          {/* Template */}
          <div className="qp-field">
            <label className="qp-field__label">
              Selected Template
              <span className="qp-field__link" onClick={openTemplateDialog}>Change</span>
            </label>
            <div
              className="qp-template-preview-bar"
              onClick={openTemplateDialog}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') openTemplateDialog(); }}
            >
              <span className="qp-template-preview-bar__icon"><FiRuby size={14} /></span>
              <span className="qp-template-preview-bar__name">{templateLabel}</span>
              {selectedCustomTemplate > 0 && (
                <span className="qp-template-preview-bar__badge">Custom</span>
              )}
            </div>
          </div>

          {/* Signature — custom portal dropdown */}
          <div className="qp-field">
            <label className="qp-field__label">Select Signature</label>
            <div
              ref={sigRef}
              className={`qp-field__input qp-multiselect${sigOpen ? ' qp-multiselect--open' : ''}`}
              onClick={openSig}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') openSig(); }}
              style={{ cursor: 'pointer' }}
            >
              <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: teamMember ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {teamMember || '— Select signature —'}
              </span>
              <FiChevronDown size={13} className={`qp-multiselect__chevron${sigOpen ? ' qp-multiselect__chevron--open' : ''}`} />
            </div>

            {sigOpen && sigRect && createPortal(
              <motion.div
                ref={sigDropdownRef}
                className="qp-multiselect__dropdown"
                style={{ position: 'fixed', top: sigRect.bottom + 4, left: sigRect.left, width: sigRect.width, zIndex: 99999 }}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.14 }}
              >
                <div
                  className="qp-multiselect__option"
                  onClick={() => { onSignatureChange(''); setSigOpen(false); }}
                >
                  <span className="qp-multiselect__option-check" />
                  <span className="qp-multiselect__option-name" style={{ color: 'var(--text-muted)' }}>— Select signature —</span>
                </div>
                {availableMembers.map((m: any, i: number) => (
                  <div
                    key={m.id || i}
                    className={`qp-multiselect__option${teamMember === m.name ? ' qp-multiselect__option--selected' : ''}`}
                    onClick={() => { onSignatureChange(m.name); setSigOpen(false); }}
                  >
                    <span className="qp-multiselect__option-check">
                      {teamMember === m.name && <FiCheck size={11} />}
                    </span>
                    <span className="qp-multiselect__option-name">{m.name}</span>
                    {m.role && <span className="qp-multiselect__option-role">{m.role}</span>}
                  </div>
                ))}
              </motion.div>,
              document.body
            )}

            {selectedSignatureMember && (
              <div className="qp-signature-preview">
                {selectedSignatureMember.profilePhoto && (
                  <img src={selectedSignatureMember.profilePhoto} alt={selectedSignatureMember.name} className="qp-signature-preview__photo" />
                )}
                <div className="qp-signature-preview__info">
                  <span className="qp-signature-preview__name">{selectedSignatureMember.name}</span>
                  {selectedSignatureMember.role && <span className="qp-signature-preview__role">{selectedSignatureMember.role}</span>}
                  <div className="qp-signature-preview__sig">{renderSignature(selectedSignatureMember)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 3: Team Members ── */}
      <div className="qp-inner-card">
        <h3 className="qp-section__title">Team Members on Proposal</h3>
        <p className="qp-section__subtitle">Select team members to include in the proposal</p>

        <div className="qp-multiselect-wrapper" style={{ maxWidth: 480 }}>
          <div
            ref={membersRef}
            className={`qp-field__input qp-multiselect${membersOpen ? ' qp-multiselect--open' : ''}`}
            onClick={openMembers}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter') openMembers(); }}
          >
            {proposalTeamMembers.length > 0 ? (
              <div className="qp-multiselect__chips">
                {proposalTeamMembers.map((m: any, i: number) => (
                  <span key={i} className="qp-multiselect__chip">
                    {m.name}
                    <button className="qp-multiselect__chip-remove" onClick={e => { e.stopPropagation(); toggleMember(m); }}>
                      <FiX size={10} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <span className="qp-multiselect__placeholder">Select members…</span>
            )}
            <FiChevronDown size={13} className={`qp-multiselect__chevron${membersOpen ? ' qp-multiselect__chevron--open' : ''}`} />
          </div>

          {membersOpen && membersRect && createPortal(
            <motion.div
              ref={membersDropdownRef}
              className="qp-multiselect__dropdown"
              style={{ position: 'fixed', top: membersRect.bottom + 4, left: membersRect.left, width: membersRect.width, zIndex: 99999 }}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.14 }}
            >
              {availableMembers.length === 0 ? (
                <div className="qp-multiselect__empty">No team members found</div>
              ) : availableMembers.map((m: any, i: number) => {
                const isSelected = proposalTeamMembers.some((pm: any) => matchMember(pm, m));
                return (
                  <div
                    key={m.id || i}
                    className={`qp-multiselect__option${isSelected ? ' qp-multiselect__option--selected' : ''}`}
                    onClick={() => toggleMember(m)}
                  >
                    <span className="qp-multiselect__option-check">{isSelected && <FiCheck size={11} />}</span>
                    <span className="qp-multiselect__option-name">{m.name}</span>
                    {m.role && <span className="qp-multiselect__option-role">{m.role}</span>}
                  </div>
                );
              })}
            </motion.div>,
            document.body
          )}
        </div>
      </div>

      {/* ── Accordion 1: Add Video ── */}
      <div className="qp-inner-card qp-accordion">
        <button className="qp-accordion__trigger" onClick={() => setVideoExpanded(v => !v)}>
          <FiChevronDown
            size={15}
            className={`qp-accordion__chevron${videoExpanded ? ' qp-accordion__chevron--open' : ''}`}
          />
          <span className="qp-accordion__label">Add Video</span>
        </button>
        {videoExpanded && (
          <motion.div
            className="qp-accordion__body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <p className="qp-section__subtitle" style={{ marginBottom: 12 }}>
              Personalize your proposal with a video explanation to make it more engaging for your client.
            </p>
            <div className="qp-field" style={{ marginBottom: 8 }}>
              <label className="qp-field__label">Video URL</label>
              <input
                type="url"
                className="qp-field__input"
                value={videoUrl}
                onChange={e => onVideoUrlChange(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <span className="qp-field__hint" style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, display: 'block' }}>
                YouTube, Vimeo, or other video hosting services
              </span>
            </div>
            {getEmbedUrl(videoUrl) && (
              <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden' }}>
                <iframe
                  src={getEmbedUrl(videoUrl)}
                  title="Video preview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{ width: '100%', height: 280, display: 'block' }}
                />
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* ── Dynamic Links ── */}
      <div className="qp-inner-card qp-accordion">
        <button className="qp-accordion__trigger" onClick={() => setLinksExpanded(v => !v)}>
          <FiChevronDown size={15} className={`qp-accordion__chevron${linksExpanded ? ' qp-accordion__chevron--open' : ''}`} />
          <span className="qp-accordion__label">Dynamic Links</span>
          {localLinks.length > 0 && <span className="qp-accordion__badge">{localLinks.length}</span>}
        </button>

        {linksExpanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>
                Add clickable links to your proposal (e.g. payment links, client portal, videos).
              </p>

              {/* Add row */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 160 }}>
                  <label className="qp-field__label">Icon</label>
                  <button
                    ref={newIconBtnRef}
                    type="button"
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => {
                      e.stopPropagation();
                      const rect = newIconBtnRef.current?.getBoundingClientRect() || null;
                      setIconDropdownRect(rect);
                      setIconDropdownOpen(v => !v);
                      setEditIconDropdownOpen(null);
                    }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '6px 10px', cursor: 'pointer',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8, color: 'var(--text-primary, #e2e8f0)', fontSize: 13, fontFamily: 'inherit',
                    }}
                  >
                    <span>{newLinkIcon?.icon} {newLinkIcon?.label}</span>
                    <i className="fa fa-chevron-down" style={{ fontSize: 10, opacity: 0.6 }} />
                  </button>
                  {iconDropdownOpen && iconDropdownRect && createPortal(
                    <div onMouseDown={e => e.stopPropagation()} style={{
                      position: 'fixed', top: iconDropdownRect.bottom + 4, left: iconDropdownRect.left,
                      width: iconDropdownRect.width, zIndex: 99999,
                      background: 'var(--bg-surface, #1e1b3a)', border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8, overflowY: 'auto',
                      maxHeight: Math.min(280, window.innerHeight - (iconDropdownRect?.bottom ?? 0) - 12),
                      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    }}>
                      {iconOptions.map(o => (
                        <button key={o.value} type="button"
                          onClick={() => { setNewLinkIcon(o); setIconDropdownOpen(false); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                            background: newLinkIcon?.value === o.value ? 'rgba(139,92,246,0.2)' : 'none',
                            border: 'none', cursor: 'pointer', color: '#e2e8f0',
                            fontSize: 13, fontFamily: 'inherit',
                          }}>
                          <span style={{ fontSize: 17 }}>{o.icon}</span><span>{o.label}</span>
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 130 }}>
                  <label className="qp-field__label">Description</label>
                  <input type="text" className={`qp-field__input${linkErrors.description ? ' qp-field__input--error' : ''}`}
                    placeholder="e.g. Pay Invoice" value={newLinkDescription} maxLength={250}
                    onChange={e => { setNewLinkDescription(e.target.value); if (linkErrors.description) setLinkErrors(p => ({ ...p, description: '' })); }} />
                  {linkErrors.description && <span style={{ fontSize: 11, color: '#ef4444' }}>{linkErrors.description}</span>}
                </div>
                <div style={{ flex: 2, minWidth: 150 }}>
                  <label className="qp-field__label">Link</label>
                  <input type="url" className={`qp-field__input${linkErrors.url ? ' qp-field__input--error' : ''}`}
                    placeholder="https://" value={newLinkUrl}
                    onChange={e => { setNewLinkUrl(e.target.value); if (linkErrors.url) setLinkErrors(p => ({ ...p, url: '' })); }}
                    onKeyDown={e => { if (e.key === 'Enter') onAddLink(); }} />
                  {linkErrors.url && <span style={{ fontSize: 11, color: '#ef4444' }}>{linkErrors.url}</span>}
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button className="glass-btn glass-btn-primary" style={{ padding: '7px 14px', fontSize: 12 }} onClick={onAddLink}>
                    + Add
                  </button>
                </div>
              </div>

              {/* Links table */}
              {localLinks.length > 0 && (
                <div style={{ border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))', borderRadius: 10, overflow: 'hidden' }}>
                  {/* Table header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 80px', padding: '8px 12px', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
                    {['DESCRIPTION', 'LINK', 'ACTION'].map(h => (
                      <span key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.06em' }}>{h}</span>
                    ))}
                  </div>
                  {/* Rows */}
                  {localLinks.map((link: any, idx: number) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 80px', padding: '10px 12px', alignItems: 'center', borderBottom: idx < localLinks.length - 1 ? '1px solid var(--border-subtle, rgba(255,255,255,0.05))' : 'none' }}>
                      {/* Description */}
                      <div>
                        {link.isEditing ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <div>
                              <button type="button"
                                id={`edit-icon-btn-${idx}`}
                                onMouseDown={e => e.stopPropagation()}
                                onClick={e => {
                                  e.stopPropagation();
                                  const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                  setEditIconDropdownRect(rect);
                                  setEditIconDropdownOpen(editIconDropdownOpen === idx ? null : idx);
                                  setIconDropdownOpen(false);
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#e2e8f0', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                                <span style={{ fontSize: 16 }}>{link.editSelectedIcon?.icon || '🔗'}</span>
                                <i className="fa fa-chevron-down" style={{ fontSize: 9, opacity: 0.6 }} />
                              </button>
                              {editIconDropdownOpen === idx && editIconDropdownRect && createPortal(
                                <div onMouseDown={e => e.stopPropagation()} style={{
                                  position: 'fixed', top: editIconDropdownRect.bottom + 4, left: editIconDropdownRect.left,
                                  zIndex: 99999, minWidth: 160,
                                  background: 'var(--bg-surface, #1e1b3a)', border: '1px solid rgba(255,255,255,0.15)',
                                  borderRadius: 8, overflowY: 'auto',
                                  maxHeight: Math.min(280, window.innerHeight - editIconDropdownRect.bottom - 12),
                                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                }}>
                                  {iconOptions.map(o => (
                                    <button key={o.value} type="button"
                                      onClick={() => { const u = [...localLinks]; u[idx].editSelectedIcon = o; setLocalLinks(u); setEditIconDropdownOpen(null); }}
                                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: link.editSelectedIcon?.value === o.value ? 'rgba(139,92,246,0.2)' : 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit' }}>
                                      <span style={{ fontSize: 17 }}>{o.icon}</span><span>{o.label}</span>
                                    </button>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </div>
                            <input type="text" className="qp-field__input" style={{ flex: 1 }} value={link.editDescription}
                              onChange={e => { const u = [...localLinks]; u[idx].editDescription = e.target.value; setLocalLinks(u); }} />
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18, flexShrink: 0 }}>{link.selectedIcon?.icon || '🔗'}</span>
                            <span style={{ fontSize: 13, color: 'var(--text-primary, #e5e7eb)' }}>{link.description}</span>
                          </div>
                        )}
                      </div>
                      {/* URL */}
                      <div style={{ paddingRight: 8 }}>
                        {link.isEditing ? (
                          <input type="url" className="qp-field__input" value={link.editUrl}
                            onChange={e => { const u = [...localLinks]; u[idx].editUrl = e.target.value; setLocalLinks(u); }} />
                        ) : (
                          <a href={link.url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 12, color: 'var(--accent-cyan,#22d3ee)', wordBreak: 'break-all', textDecoration: 'none' }}>
                            {link.url}
                          </a>
                        )}
                      </div>
                      {/* Actions */}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                        {link.isEditing ? (
                          <>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4ade80', padding: 4, fontSize: 16 }} title="Save" onClick={() => {
                              const u = [...localLinks]; u[idx] = { ...u[idx], description: u[idx].editDescription, url: u[idx].editUrl, selectedIcon: u[idx].editSelectedIcon, isEditing: false };
                              pushLinks(u);
                            }}><i className="fa fa-check" /></button>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', padding: 4, fontSize: 16 }} title="Cancel" onClick={() => { const u = [...localLinks]; u[idx].isEditing = false; setLocalLinks(u); }}>
                              <i className="fa fa-times" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93c5fd', padding: 4, fontSize: 15 }} title="Edit" onClick={() => {
                              const u = [...localLinks]; u[idx] = { ...u[idx], isEditing: true, editDescription: u[idx].description, editUrl: u[idx].url, editSelectedIcon: u[idx].selectedIcon }; setLocalLinks(u);
                            }}><i className="fa fa-pencil" style={{ fontSize: 13 }} /></button>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', padding: 4, fontSize: 15 }} title="Delete" onClick={() => pushLinks(localLinks.filter((_: any, i: number) => i !== idx))}>
                              <i className="fa fa-trash" style={{ fontSize: 13 }} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* ── Accordion 2: Extra Documents ── */}
      <div className="qp-inner-card qp-accordion">
        <button className="qp-accordion__trigger" onClick={() => setExtraDocsExpanded(v => !v)}>
          <FiChevronDown
            size={15}
            className={`qp-accordion__chevron${extraDocsExpanded ? ' qp-accordion__chevron--open' : ''}`}
          />
          <span className="qp-accordion__label">Upload Extra Documents</span>
          {extraDocumentList.length > 0 && (
            <span className="qp-accordion__badge">{extraDocumentList.length}</span>
          )}
        </button>
        {extraDocsExpanded && (
          <motion.div
            className="qp-accordion__body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <UploadDocumentZone
              onFileDrop={onExtraDocFileDrop}
              onInvalidFile={() => onInvalidPdfFile?.()}
              uploading={uploadDocLoading}
              compact={true}
            />
            {!transactionId && (
              <p className="qp-field__error" style={{ marginTop: 6 }}>
                Please complete Step 1 first before uploading extra documents
              </p>
            )}

            {extraDocumentList.length > 0 && (
              <table className="qp-table qp-table--compact" style={{ marginTop: 'var(--space-3)', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="qp-table__th">File Name</th>
                    <th className="qp-table__th" style={{ width: 80, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {extraDocumentList.map((doc, i) => (
                    <tr key={i} className="qp-table__row">
                      <td className="qp-table__td">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FiFileText size={12} style={{ color: '#ef4444' }} />
                          <span style={{ fontSize: 12 }}>{doc.fileName}</span>
                        </div>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          {doc.fileURL && (
                            <a
                              href={doc.fileURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="qp-table__action-btn"
                              title="Preview"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                            >
                              <FiEye size={12} />
                            </a>
                          )}
                          <button
                            className="qp-table__action-btn"
                            onClick={() => onExtraDocRemove(i)}
                            title="Remove"
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </motion.div>
        )}
      </div>
      </div>{/* end qp-step-shell */}

      {/* ── Template picker dialog ── */}
      {templateDialogOpen && (
        <div className="qp-dialog-overlay" onClick={() => setTemplateDialogOpen(false)}>
          <motion.div
            className="qp-dialog qp-dialog--wide"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>Select Template</h3>
              <button className="qp-dialog__close" onClick={() => setTemplateDialogOpen(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="qp-dialog__body">
              <div className="qp-template-grid">
                {TEMPLATE_OPTIONS.map(tpl => {
                  const isSelected = pendingTemplate?.id === tpl.id && !pendingTemplate?.customId;
                  return (
                    <div
                      key={tpl.id}
                      className={`qp-template-card${isSelected ? ' qp-template-card--selected' : ''}`}
                      onClick={() => setPendingTemplate({ id: tpl.id, name: tpl.label, images: [], customId: 0 })}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') setPendingTemplate({ id: tpl.id, name: tpl.label, images: [], customId: 0 }); }}
                    >
                      <div className="qp-template-card__thumb">
                        <img src={tpl.img} alt={tpl.label} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                        <div className="qp-template-card__overlay">
                          <button
                            className="qp-template-card__preview-btn"
                            onClick={e => { e.stopPropagation(); setPreviewGalleria({ title: tpl.label, images: tpl.previewImages, idx: 0 }); }}
                          >
                            <FiEye size={12} /> Preview
                          </button>
                        </div>
                      </div>
                      <span className="qp-template-card__name">{tpl.label}</span>
                      <span className="qp-template-card__desc">{tpl.desc}</span>
                      {isSelected && <span className="qp-template-card__check"><FiCheck size={13} /></span>}
                    </div>
                  );
                })}
                {customTemplateList.map((ct: any) => {
                  const images: string[] = (() => {
                    if (!ct.images) return [];
                    if (Array.isArray(ct.images))
                      return typeof ct.images[0] === 'string'
                        ? (ct.images[0] as string).split(',').map((s: string) => s.trim()).filter(Boolean)
                        : (ct.images as string[]);
                    return (ct.images as string).split(',').map((s: string) => s.trim()).filter(Boolean);
                  })();
                  const isSelected = pendingTemplate?.customId === ct.id;
                  return (
                    <div
                      key={`custom-${ct.id}`}
                      className={`qp-template-card${isSelected ? ' qp-template-card--selected' : ''}`}
                      onClick={() => setPendingTemplate({ id: String(ct.parentTemplateId || 1), name: ct.name || ct.title || 'Custom', images, customId: ct.id })}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') setPendingTemplate({ id: String(ct.parentTemplateId || 1), name: ct.name || ct.title || 'Custom', images, customId: ct.id }); }}
                    >
                      <div className="qp-template-card__thumb">
                        {images[0]
                          ? <img src={images[0]} alt={ct.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                          : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Custom</span>
                        }
                        {images.length > 0 && (
                          <div className="qp-template-card__overlay">
                            <button
                              className="qp-template-card__preview-btn"
                              onClick={e => { e.stopPropagation(); setPreviewGalleria({ title: ct.name || ct.title, images, idx: 0 }); }}
                            >
                              <FiEye size={12} /> Preview
                            </button>
                          </div>
                        )}
                      </div>
                      <span className="qp-template-card__name">{ct.name || ct.title}</span>
                      <span className="qp-template-card__desc">{ct.description || 'Custom template'}</span>
                      <span className="qp-template-preview-bar__badge" style={{ position: 'absolute', top: 8, right: 8 }}>Custom</span>
                      {isSelected && <span className="qp-template-card__check"><FiCheck size={13} /></span>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="qp-dialog__footer">
              <button
                className="qp-actions__cancel glass-btn"
                onClick={() => setTemplateDialogOpen(false)}
                disabled={isSavingTemplate}
              >
                Cancel
              </button>
              <button
                className="qp-actions__next glass-btn glass-btn-primary"
                onClick={handleTemplateSave}
                disabled={isSavingTemplate}
              >
                {isSavingTemplate ? (
                  <><div className="qp-spinner qp-spinner--small" /> Saving…</>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Galleria preview portal ── */}
      {previewGalleria && createPortal(
        <div className="qp-galleria-overlay" onClick={() => setPreviewGalleria(null)}>
          <div className="qp-galleria" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="qp-galleria__header">
              <span>{previewGalleria.title} — Preview</span>
              <button className="qp-modal__close" onClick={() => setPreviewGalleria(null)}>
                <FiX size={18} />
              </button>
            </div>

            {/* Main stage */}
            <div className="qp-galleria__stage">
              {previewGalleria.images.length > 1 && (
                <button
                  className="qp-galleria__nav qp-galleria__nav--prev"
                  onClick={() => setPreviewGalleria(p => p ? { ...p, idx: (p.idx - 1 + p.images.length) % p.images.length } : null)}
                >
                  <FiChevronLeft size={22} />
                </button>
              )}
              <img
                src={previewGalleria.images[previewGalleria.idx]}
                alt={`Preview ${previewGalleria.idx + 1}`}
                className="qp-galleria__main-img"
              />
              {previewGalleria.images.length > 1 && (
                <button
                  className="qp-galleria__nav qp-galleria__nav--next"
                  onClick={() => setPreviewGalleria(p => p ? { ...p, idx: (p.idx + 1) % p.images.length } : null)}
                >
                  <FiChevronRight size={22} />
                </button>
              )}
              {previewGalleria.images.length > 1 && (
                <span className="qp-galleria__counter">
                  {previewGalleria.idx + 1} / {previewGalleria.images.length}
                </span>
              )}
            </div>

            {/* Thumbnail strip — only when multiple images */}
            {previewGalleria.images.length > 1 && (
              <div className="qp-galleria__thumbs">
                {previewGalleria.images.map((img, i) => (
                  <button
                    key={i}
                    className={`qp-galleria__thumb${i === previewGalleria.idx ? ' qp-galleria__thumb--active' : ''}`}
                    onClick={() => setPreviewGalleria(p => p ? { ...p, idx: i } : null)}
                  >
                    <img src={img} alt={`Thumb ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default React.memo(Step2_SignatureDocsVideo);
